import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, SubmitHandler } from 'react-hook-form';

import { 
  CameraIcon, 
  XMarkIcon,
  CloudArrowUpIcon,
  HomeIcon,           // Add this import
  DevicePhoneMobileIcon, // Add this import
  GlobeAltIcon,       // Add this import
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { updateProfile, uploadProfilePhoto } from '../store/slices/userSlice';
import { fetchCurrentUser } from '../store/slices/authSlice';
import Button from '../components/common/Button';
import LocationPicker from '../components/common/LocationPicker';
import { fetchUserRoleSKills } from '../store/slices/genericSlice'
import { Skill } from '../types';
import SkillSelector from '../components/common/SkillSelector';
import { SKILL_CATEGORIES } from '../data/skillCategories';
import Switch from '../components/common/Switch';
import { compressImage } from '../utils/compression';
import toast from 'react-hot-toast';
import { calculateProfileCompletionScore, getProfileFieldsChecklist, hasCompletedRequiredProfile } from '../utils/profileCompletion';
import { UserIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


interface EditProfileFormData {
    first_name: string;
    last_name?: string;
    bio?: string;
    location: string;
    phone?: string;
    email?: string;
    website?: string;
    experience?: 'beginner' | 'intermediate' | 'professional';
    hourly_rate?: number;
    is_available?: boolean;
    latitude?: number;
    longitude?: number;
    display_phone_number?: boolean;
    display_email?: boolean;
    user_type?: 'creator' | 'model' | 'hirer';
    creator_sub_type?: 'photographer' | 'videographer' | 'both';
  }
  
  const schema: yup.ObjectSchema<EditProfileFormData> = yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    bio: yup.string().optional().max(500, 'Bio must be less than 500 characters'),
    location: yup.string().required('Location is required'),
    phone: yup.string().required('Phone number is required'),
    email: yup.string(),
    website: yup.string().optional().url('Please enter a valid URL'),
    experience: yup.mixed<'beginner' | 'intermediate' | 'professional'>().optional(),
    hourly_rate: yup.number().optional().min(0, 'Rate must be positive'),
    is_available: yup.boolean().optional(),
    latitude: yup.number(),
    longitude: yup.number(),
    display_phone_number: yup.boolean().optional(),
    display_email: yup.boolean().optional(),
    user_type: yup.mixed<'creator' | 'model' | 'hirer'>().optional(),
    creator_sub_type: yup.mixed<'photographer' | 'videographer' | 'both'>().optional(),
  });

// type EditProfileFormData = yup.InferType<typeof schema>;

const EditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, role } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector((state) => state.user);
  const { skills } = useAppSelector((state) => state.generic);
  
  const [selectedSpecializations, setSelectedSpecializations] = useState<Skill[]>(
    user?.skills || []
  );
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<number[] | []>([]);
  const [availableCities, setAvailableCities] = useState<{city_name: string, latitude: number, longitude: number}[]>(user?.available_cities || []);
  const [cityInput, setCityInput] = useState('');
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [currentScore, setCurrentScore] = useState(0);


  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<EditProfileFormData>({ // Add the type here
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      bio: user?.bio || '',
      location: user?.location || '',
      phone: user?.phone || '',
      email: user?.email || '',
      website: user?.website || '',
      experience: user?.experience || 'beginner',
      hourly_rate: user?.hourly_rate || 0,
      is_available: user?.is_available ?? true,
      latitude: user?.latitude,
      longitude: user?.longitude,
      display_phone_number: user?.display_phone_number,
      display_email: user?.display_email,
      user_type: user?.user_type || 'hirer',
      creator_sub_type: user?.creator_sub_type || undefined,
    },
  });

    useEffect(() => {
       setSelectedSkills(user?.specializations ?? [])
       dispatch(fetchUserRoleSKills(role))
      }, [dispatch]);

  // const skills = user?.user_type === 'photographer' 
  //   ? ['Portrait', 'Wedding', 'Commercial', 'Fashion', 'Landscape', 'Street', 'Documentary', 'Fine Art', 'Product', 'Event']
  //   : ['Fashion', 'Commercial', 'Fitness', 'Beauty', 'Runway', 'Plus Size', 'Editorial', 'Lifestyle', 'Glamour', 'Art'];

  // Profile Image Dropzone
  const profileImageDropzone = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        try {
            const compressedFile = await compressImage(file, { maxSizeMB: 0.8 });
            const formData = new FormData();
            formData.append('image', compressedFile);
            dispatch(uploadProfilePhoto({ type: 'profile', file: formData }));
            
            // Preview
            const reader = new FileReader();
            reader.onload = () => setProfileImagePreview(reader.result as string);
            reader.readAsDataURL(compressedFile);
        } catch (error) {
            console.error('Compression failed', error);
            const formData = new FormData();
            formData.append('image', file);
            dispatch(uploadProfilePhoto({ type: 'profile', file: formData }));
            
            // Preview
            const reader = new FileReader();
            reader.onload = () => setProfileImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
      }
    },
  });

  // Cover Image Dropzone
  const coverImageDropzone = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        try {
            const compressedFile = await compressImage(file, { maxSizeMB: 0.8 });
            const formData = new FormData();
            formData.append('image', compressedFile);
            dispatch(uploadProfilePhoto({ type: 'cover', file: formData }));
            
            // Preview
            const reader = new FileReader();
            reader.onload = () => setCoverImagePreview(reader.result as string);
            reader.readAsDataURL(compressedFile);
        } catch (error) {
            console.error('Compression failed', error);
            const formData = new FormData();
            formData.append('image', file);
            dispatch(uploadProfilePhoto({ type: 'cover', file: formData }));
            
            // Preview
            const reader = new FileReader();
            reader.onload = () => setCoverImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
      }
    },
  });

  const handleSpecializationToggle = (spec: Skill) => {
    setSelectedSpecializations(prev => 
      prev.includes(spec) 
        ? prev.filter(s => s !== spec)
        : [...prev, spec]
    );
  };

  const scrollToError = (fieldName?: string) => {
    setTimeout(() => {
      if (fieldName) {
        const el = 
          document.querySelector(`[name="${fieldName}"]`) || 
          document.getElementById(fieldName) || 
          document.querySelector(`[data-field="${fieldName}"]`) ||
          (fieldName === 'location' && (document.getElementById('location-picker-wrapper') || document.getElementById('location')));

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusable = (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') ? el : el.querySelector('input, textarea, button');
          if (focusable) {
            (focusable as HTMLElement).focus?.();
          }
          return;
        }
      }
      const firstErrorEl = document.querySelector('.text-red-600, .text-red-500');
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const onError = (formErrors: any) => {
    const firstErrorKey = Object.keys(formErrors)[0];
    scrollToError(firstErrorKey);
  };

  const onSubmit: SubmitHandler<EditProfileFormData> = async (data) => {
    const updateData = {
      ...data,
      specializations: selectedSkills,
      available_cities: availableCities,
    };

    const profileInput = {
      first_name: data.first_name,
      last_name: data.last_name,
      location: data.location,
      profile_pic: profileImagePreview || user?.profile_pic || user?.profile_pic_signed || '',
      bio: data.bio,
      specializations: selectedSkills,
      phone: data.phone,
      website: data.website,
      experience: data.experience,
    };

    if (!hasCompletedRequiredProfile(profileInput)) {
      const projectedScore = calculateProfileCompletionScore(profileInput);
      setCurrentScore(projectedScore);
      setShowIncompleteModal(true);
      
      // Identify the first missing REQUIRED field to scroll to
      let missingField = '';
      if (!data.first_name?.trim()) missingField = 'first_name';
      else if (!data.last_name?.trim()) missingField = 'last_name';
      else if (!data.location?.trim()) missingField = 'location';
      else if (!data.phone?.trim()) missingField = 'phone';
      else if (!selectedSkills || selectedSkills.length === 0) missingField = 'specializations';

      scrollToError(missingField);
      return;
    }
    
    const result = await dispatch(updateProfile(updateData));
    if (updateProfile.fulfilled.match(result)) {
      if (user?.id) dispatch(fetchCurrentUser(user.id));
      navigate('/profile');
    }
  };

  return (
    <div className="page-container h-full overflow-y-auto px-4 md:px-6">
      <div className="content-wrapper py-8 md:py-20">
        <div className="w-full mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Edit Profile</h1>
            <p className="text-gray-600 dark:text-gray-400">Update your information to attract better opportunities.</p>
          </motion.div>

          <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-8">

            {/* Basic Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    {...register('first_name')}
                    type="text"
                    className="input-field"
                    placeholder="John"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name
                  </label>
                  <input
                    {...register('last_name')}
                    type="text"
                    className="input-field"
                    placeholder="Doe"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bio
                  </label>
                  <textarea
                    {...register('bio')}
                    rows={4}
                    className="input-field resize-none"
                    placeholder="Tell people about yourself and your work..."
                  />
                  {errors.bio && (
                    <p className="mt-1 text-sm text-red-600">{errors.bio.message}</p>
                  )}
                </div>

                <div id="location-picker-wrapper" data-field="location">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Location *
                    </label>
                    <LocationPicker
                        id="location"
                        name="location"
                        value={watch('location') || ''}
                        initialCoordinates={ {
                          lat: user?.latitude,
                          lng: user?.longitude,
                        }}
                        onChange={(location, coordinates?: { lat: number; lng: number }) => {
                        setValue('location', location);
                        setValue('latitude', coordinates?.lat);
                        setValue('longitude', coordinates?.lng)

                        // Optionally store coordinates if needed
                        if (coordinates) {
                            // console.log('Selected coordinates:', coordinates);
                        }
                        }}
                        placeholder="Enter your city or location..."
                        className="w-full"
                    />
                    {errors.location && (
                        <p className="text-red-600 text-sm mt-1">{errors.location.message}</p>
                    )}
                </div>


                {/* Available Cities - NEW */}
                {user?.user_type !== 'hirer' && (
                  <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Available Cities
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                          Add cities where you are available for work.
                      </p>
                      
                      <div className="space-y-3">
                          <LocationPicker
                              value={cityInput}
                              onChange={(location, coordinates) => {
                                  setCityInput(location);
                                  if (location && coordinates) {
                                      const newCity = {
                                          city_name: location.split(',')[0], // Use just the city name for display if possible, or full location
                                          latitude: coordinates.lat,
                                          longitude: coordinates.lng
                                      };
                                      
                                      // Check if city already exists
                                      const exists = availableCities.some(
                                          c => c.city_name === newCity.city_name
                                      );
                                      
                                      if (!exists) {
                                          setAvailableCities([...availableCities, newCity]);
                                          setCityInput(''); // Clear input after selection
                                      }
                                  }
                              }}
                              placeholder="Add a city..."
                              className="w-full"
                          />

                          {/* Selected Cities List */}
                          <div className="flex flex-wrap gap-2">
                              {availableCities.map((city, index) => (
                                  <div 
                                      key={index}
                                      className="flex items-center bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 px-3 py-1.5 rounded-full text-sm border border-primary-100 dark:border-primary-800"
                                  >
                                      <MapPinIcon className="h-4 w-4 mr-1.5" />
                                      <span>{city.city_name}</span>
                                      <button
                                          type="button"
                                          onClick={() => {
                                              setAvailableCities(availableCities.filter((_, i) => i !== index));
                                          }}
                                          className="ml-2 p-0.5 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-full transition-colors"
                                      >
                                          <XMarkIcon className="h-4 w-4" />
                                      </button>
                                  </div>
                              ))}
                          </div>
                      </div>
                  </div>
                )}

                {/* Phone Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone
                    </label>
                    <div className="relative">
                    <DevicePhoneMobileIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <input
                        {...register('phone')}
                        type="tel"
                        className="input-field pl-10"
                        placeholder="Your phone number"
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setValue('phone', value);
                        }}
                    />
                    </div>
                    {errors.phone && (
                    <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
                    )}

                    {/* Privacy Switch for Phone */}
  {/* <div className="mt-2 pl-1">
  <Switch
  checked={watch('display_phone_number') ?? false}
  onChange={(value) => setValue('display_phone_number', value)}
  label="Display phone number on profile for others"
  labelPosition="right"
  size="sm"
/>
  </div> */}
                </div>

                {/* If you don't have an email input field, add it here or skip this section */}
{/* <div>
  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
    Email
  </label>
  <div className="relative">
  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
  <input
    type="email"
    id="email"
    {...register('email')}
    className="input-field pl-10"
    placeholder="your.email@example.com"
  />
  </div>
  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                    )}
  
  {/* Privacy Switch for Email
  <div className="mt-2 pl-1">
  <Switch
  checked={watch('display_email') ?? false}
  onChange={(value) => setValue('display_email', value)}
  label="Display email on profile for others"
  labelPosition="right"
  size="sm"
  />
  </div> 
</div> */}

                {/* Website Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website
                    </label>
                    <div className="relative">
                    <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <input
                        {...register('website')}
                        type="url"
                        className="input-field pl-10"
                        placeholder="https://your-website.com"
                    />
                    </div>
                    {errors.website && (
                    <p className="text-red-600 text-sm mt-1">{errors.website.message}</p>
                    )}
                </div>

              </div>
            </motion.div>

            {/* Specializations - Hide for Hirer Users */}
            {user?.user_type !== 'hirer' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="card h-300"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {user?.user_type === 'creator' ? 'Creator' : 'Modeling'} Specializations
              </h2>
              <SkillSelector
                selectedSkillIds={selectedSkills}
                onChange={(values) => setSelectedSkills(values)}
                options={skills}
                placeholder="Select specializations..."
                maxSkills={10}
                categories={SKILL_CATEGORIES}
              />
            </motion.div>
            )}

            {/* Professional Information - Hide for Hirer Users */}
            {user?.user_type !== 'hirer' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Professional Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {user?.user_type === 'creator' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Specialty Tag (I identify myself primarily as a...)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['photographer', 'videographer', 'both'] as const).map((sub) => (
                        <label key={sub} className={`
                          relative cursor-pointer rounded-lg border p-3 text-center text-sm transition-all duration-200 block
                          ${watch('creator_sub_type') === sub
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-300 font-semibold' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900'
                          }
                        `}>
                          <input
                            {...register('creator_sub_type')}
                            type="radio"
                            value={sub}
                            className="sr-only"
                          />
                          {sub === 'photographer' && 'Photographer'}
                          {sub === 'videographer' && 'Videographer'}
                          {sub === 'both' && 'Photographer - Videographer'}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Experience Level
                  </label>
                  <select {...register('experience')} className="input-field">
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hourly Rate (Rs)
                  </label>
                  <input
                    {...register('hourly_rate')}
                    type="number"
                    min="0"
                    className="input-field"
                    placeholder="50"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                  <Switch
                    checked={watch('is_available') ?? false}
                    onChange={(value) => setValue('is_available', value)}
                    label="I am currently available for new projects"
                    labelPosition="right"
                    size="md"
                  />
                  </label>
                </div>
              </div>
            </motion.div>
            )}

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex space-x-4"
            >
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                className="flex-1"
              >
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate('/profile')}
                className="flex-1"
              >
                Cancel
              </Button>
            </motion.div>
          </form>
        </div>
      </div>

      {/* Profile Details Required Modal */}
      {showIncompleteModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl border border-gray-100 dark:border-gray-800 relative overflow-hidden">
            <button
              onClick={() => setShowIncompleteModal(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-400 to-amber-500" />
            
            <div className="w-14 h-14 bg-orange-50 dark:bg-orange-950/30 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-orange-100/50 dark:border-orange-900/30">
              <UserIcon className="w-7 h-7 text-orange-500" />
            </div>
            
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 tracking-tight">
              Complete Required Details
            </h3>
            
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              Please complete your profile details below to save changes and unlock platform features:
            </p>

            {/* Checklist of missing details */}
            <div className="space-y-2 mb-6 text-left max-h-60 overflow-y-auto pr-1">
              {getProfileFieldsChecklist({
                first_name: watch('first_name'),
                last_name: watch('last_name'),
                location: watch('location'),
                bio: watch('bio'),
                specializations: selectedSkills,
                profile_pic: profileImagePreview || user?.profile_pic || user?.profile_pic_signed || '',
                phone: watch('phone'),
                experience: watch('experience'),
                website: watch('website'),
              }).map((item) => (
                <div 
                  key={item.id} 
                  className={`flex items-center justify-between p-2.5 rounded-xl border text-xs font-medium transition-all ${
                    item.isFilled 
                      ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300' 
                      : item.isRequired 
                        ? 'bg-orange-50/60 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900/40 text-orange-900 dark:text-orange-200'
                        : 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700/60 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {item.isFilled ? (
                      <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : item.isRequired ? (
                      <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                    )}
                    {item.name}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${
                    item.isFilled 
                      ? 'text-emerald-600 dark:text-emerald-400' 
                      : item.isRequired 
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {item.isFilled ? 'Completed' : item.isRequired ? 'Required' : 'Optional'}
                  </span>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => {
                setShowIncompleteModal(false);
                let missingField = '';
                const values = watch();
                if (!values.first_name?.trim()) missingField = 'first_name';
                else if (!values.last_name?.trim()) missingField = 'last_name';
                else if (!values.location?.trim()) missingField = 'location';
                else if (!values.phone?.trim()) missingField = 'phone';
                else if (!selectedSkills || selectedSkills.length === 0) missingField = 'specializations';
                scrollToError(missingField);
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 px-6 rounded-2xl transition-all shadow-lg shadow-orange-500/20 text-xs uppercase tracking-widest active:scale-95 duration-150"
            >
              Fill Missing Fields
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfilePage;
